import type { components } from '@/generated/core-api'

import { CoreError } from './errors'
import { API_ROOT, asRecord, request } from './transport'

type WireAccountUISetting = components['schemas']['AccountUISetting']
type WireUpdateAccountUISettingRequest = components['schemas']['UpdateAccountUISettingRequest']

export interface AccountUISetting {
  scope: string
  scopeKey: string | null
  payload: Record<string, unknown>
  revision: number
  updatedAt: string | null
}

export async function getAccountUISetting(
  scope: string,
  scopeKey: string | null,
  signal?: AbortSignal,
): Promise<AccountUISetting> {
  const body = await request('GET', `${API_ROOT}/account/ui-settings`, {
    query: { scope, ...(scopeKey ? { scopeKey } : {}) },
    ...(signal ? { signal } : {}),
  })
  return readAccountUISetting(body)
}

/**
 * `expectedRevision` IS THE WHOLE SAFETY OF THIS WRITE.
 *
 * The payload is a full replacement, so a caller sending one it composed from
 * an older read does not merge with what is stored — it erases it. The
 * revision states which stored setting the payload was built on; the server
 * refuses it with conflict once that is no longer the current one. 0 means the
 * writer read no stored setting, which is what a missing row answers, so a
 * blind write can only ever create.
 */
export async function saveAccountUISetting(
  scope: string,
  scopeKey: string | null,
  payload: Record<string, unknown>,
  expectedRevision: number,
): Promise<AccountUISetting> {
  const body = {
    scope,
    scopeKey,
    payload,
    expectedRevision,
  } satisfies WireUpdateAccountUISettingRequest
  return readAccountUISetting(
    await request('PUT', `${API_ROOT}/account/ui-settings`, {
      body,
      // UI-setting payloads are server-bounded well below the Fetch keepalive
      // limit. The hook flushes its debounce on unmount, and this is what lets
      // that final per-account choice finish during a reload instead of being
      // discarded with the document that initiated it.
      keepalive: true,
    }),
  )
}

function readAccountUISetting(body: unknown): AccountUISetting {
  const wire = body as WireAccountUISetting | undefined
  const payload = asRecord(wire?.payload)
  if (
    !wire ||
    typeof wire.scope !== 'string' ||
    (wire.scopeKey !== null && typeof wire.scopeKey !== 'string') ||
    !payload ||
    typeof wire.revision !== 'number' ||
    (wire.updatedAt !== null && typeof wire.updatedAt !== 'string')
  ) {
    throw new CoreError('malformed', 'account UI setting response is incomplete')
  }
  return {
    scope: wire.scope,
    scopeKey: wire.scopeKey,
    payload,
    revision: wire.revision,
    updatedAt: wire.updatedAt,
  }
}
